import React, { useState, useEffect } from "react";
import {
  Package,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Search,
  Edit3,
  Save,
  X,
  Plus,
  Minus,
  BarChart3,
  RefreshCw,
  Trash2,
  List,
  CheckCircle,
  XCircle,
} from "lucide-react";

import productAvailabilityService from "../../../services/api/admin.product.service";

// Toast Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000); // Auto-close after 5 seconds
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500';
  const Icon = type === 'success' ? CheckCircle : XCircle;

  return (
    <div className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 min-w-80 max-w-md`}>
      <Icon className="h-5 w-5 flex-shrink-0" />
      <p className="flex-1 text-sm font-medium">{message}</p>
      <button
        onClick={onClose}
        className="text-white hover:text-gray-200 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// Toast Container Component
const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed bottom-6 right-6 z-50 space-y-3">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className="transform transition-all duration-300 ease-in-out animate-slide-in-right"
        >
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        </div>
      ))}
    </div>
  );
};

const ProductAvailabilityManager = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // Toast state
  const [toasts, setToasts] = useState([]);

  // Toast functions
  const addToast = (message, type = 'success') => {
    const id = Date.now() + Math.random(); // Unique ID
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const showSuccess = (message) => addToast(message, 'success');
  const showError = (message) => addToast(message, 'error');

  const getSafeProductData = (product) => {
    const availableUnits = product.availability?.availableUnits ?? "";
    const lowStockThreshold = product.availability?.lowStockThreshold ?? "";

    return {
      basicData: {
        legalName: product.basicData?.legalName || "",
        secondaryName: product.basicData?.secondaryName || "",
        isin: product.basicData?.isin || "",
        cin: product.basicData?.cin || "",
        industry: product.basicData?.industry || "",
        sector: product.basicData?.sector || "",
        dateOfIncorporation: product.basicData?.dateOfIncorporation || null,
        website: product.basicData?.website || "",
        uniqueTitle: product.basicData?.uniqueTitle || "",
        logo: product.logo || product.basicData?.logo || "",
      },
      weekData52: {
        buyPrice: product.weekData52?.buyPrice || 0,
        sellPrice: product.weekData52?.sellPrice || 0,
      },
      availability: {
        availableUnits: availableUnits,
        lowStockThreshold: lowStockThreshold,
        trackingEnabled: product.availability?.trackingEnabled ?? true,
        // Calculate status based on available units and threshold
        status: calculateProductStatus(availableUnits, lowStockThreshold),
        reservedUnits: product.availability?.reservedUnits || 0,
        soldUnits: product.availability?.soldUnits || 0,
      },
    };
  };

  // Add this helper function to calculate status based on available units
  const calculateProductStatus = (availableUnits, lowStockThreshold) => {
    if (availableUnits === 0) {
      return "out-of-stock";
    } else if (availableUnits <= lowStockThreshold) {
      return "low-stock";
    } else {
      return "in-stock";
    }
  };
  const [currentEditingProduct, setCurrentEditingProduct] = useState(() =>
    getSafeProductData({
      availability: { availableUnits: "", lowStockThreshold: "" },
      weekData52: { buyPrice: "", sellPrice: "" }
    })
  );
  // Form states for editing
  const [editForm, setEditForm] = useState({
    // totalUnits: "",
    lowStockThreshold: "",
    trackingEnabled: true,
  });
  // Create form state
  const [createForm, setCreateForm] = useState({
    basicData: {
      legalName: "",
      secondaryName: "",
      isin: "",
      cin: "",
      industry: "",
      sector: "",
      logo: "",
    },
    weekData52: {
      buyPrice: "",
      sellPrice: "",
    },
    availability: {
      availableUnits: "", // Only available units
      lowStockThreshold: "",
      trackingEnabled: true,
    },
  });
  // Transaction forms
  const [reserveQuantity, setReserveQuantity] = useState("");
  const [sellQuantity, setSellQuantity] = useState("");

  // Pending logo file for create mode (uploaded after product is created)
  const [pendingLogoFile, setPendingLogoFile] = useState(null);

  // Validate and store image file + show a local preview (create mode)
  const handleCreateLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showError("Please select a valid image file (PNG, JPG, SVG, etc.)");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showError("Logo file must be smaller than 2 MB");
      return;
    }
    // Store the raw File object for later upload
    setPendingLogoFile(file);
    // Show a local preview immediately
    const reader = new FileReader();
    reader.onload = (ev) =>
      setCreateForm((prev) => ({
        ...prev,
        basicData: { ...prev.basicData, logo: ev.target.result },
      }));
    reader.readAsDataURL(file);
  };

  // Validate, then upload immediately to the backend (edit mode)
  const handleEditLogoChange = async (e, uniqueTitle) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showError("Please select a valid image file (PNG, JPG, SVG, etc.)");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showError("Logo file must be smaller than 2 MB");
      return;
    }
    // Show optimistic preview right away
    const reader = new FileReader();
    reader.onload = (ev) =>
      setCurrentEditingProduct((prev) => ({
        ...prev,
        basicData: { ...prev.basicData, logo: ev.target.result },
      }));
    reader.readAsDataURL(file);

    // Upload to backend
    const result = await productAvailabilityService.uploadProductLogo(
      uniqueTitle,
      file
    );
    if (result.success) {
      showSuccess("Logo uploaded successfully");
      setCurrentEditingProduct((prev) => ({
        ...prev,
        basicData: { ...prev.basicData, logo: result.logo },
      }));
    } else {
      showError(result.message || "Failed to upload logo");
    }
  };

  useEffect(() => {
    loadData();
  }, [activeTab, currentPage]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === "overview") {
        const statsResponse =
          await productAvailabilityService.getAvailabilityStats();
        if (statsResponse.success) {
          setStats(statsResponse.data);
        } else {
          showError("Failed to load statistics");
        }
      } else if (activeTab === "low-stock") {
        // getLowStockProducts now supports search parameter
        const lowStockResponse =
          await productAvailabilityService.getLowStockProducts(
            currentPage,
            10,
            searchTerm
          );

        if (lowStockResponse.success) {
          // Apply status calculation to low stock products as well
          const processedProducts = lowStockResponse.data.map((product) => {
            return getSafeProductData(product);
          });

          // Filter to only show products that are actually low-stock or out-of-stock
          const filteredProducts = processedProducts.filter(product =>
            product.availability.status === "low-stock" ||
            product.availability.status === "out-of-stock"
          );

          setLowStockProducts(filteredProducts);
          setPagination(lowStockResponse.pagination);
        } else {
          showError("Failed to load low stock products");
        }

      } else if (activeTab === "all-products") {
        const response = await productAvailabilityService.getAllProducts({
          page: currentPage,
          searchTerm: searchTerm,
        });
        if (response.success) {
          setAllProducts((prevProducts) => {
            return response.data.map((product) => {
              const safeProduct = getSafeProductData(product);
              const prevProduct = prevProducts.find(
                (p) => p._id === product._id
              );
              return {
                ...safeProduct,
                _id: product._id,
                statusChanged:
                  prevProduct &&
                  prevProduct.availability.status !==
                  safeProduct.availability.status,
              };
            });
          });
          setPagination(response.pagination);
        } else {
          showError("Failed to load products");
        }
      }
    } catch (error) {
      console.error("Error loading data:", error);
      showError(error.response?.data?.message || "An error occurred while loading data");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAvailability = async (productId) => {
    try {
      setLoading(true);
      const response = await productAvailabilityService.updateAvailability(
        productId,
        {
          availableUnits: editForm.availableUnits,
          lowStockThreshold: editForm.lowStockThreshold,
          trackingEnabled: editForm.trackingEnabled,
        }
      );
      if (response.success) {
        showSuccess("Product availability updated successfully");
        setEditingProduct(null);
        loadData();
      } else {
        showError(response.message || "Failed to update availability");
      }
    } catch (error) {
      console.error("Error updating availability:", error);
      showError(error.response?.data?.message || "Failed to update product availability");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === "all-products" || activeTab === "low-stock") {
        setCurrentPage(1); // Reset to first page when searching
        loadData();
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm, activeTab]);

  const handleReserveUnits = async (productId) => {
    if (!reserveQuantity || reserveQuantity <= 0) {
      showError("Please enter a valid quantity to reserve");
      return;
    }
    try {
      setLoading(true);
      const response = await productAvailabilityService.reserveUnits(
        productId,
        parseInt(reserveQuantity)
      );
      if (response.success) {
        showSuccess(`Successfully reserved ${reserveQuantity} units`);
        setReserveQuantity("");
        loadData(); // Refresh data to show updated status
      } else {
        showError(response.message || "Failed to reserve units");
      }
    } catch (error) {
      console.error("Error reserving units:", error);
      showError(error.response?.data?.message || "Failed to reserve units");
    } finally {
      setLoading(false);
    }
  };

  const handleSellUnits = async (productId) => {
    if (!sellQuantity || sellQuantity <= 0) {
      showError("Please enter a valid quantity to sell");
      return;
    }
    try {
      setLoading(true);
      const response = await productAvailabilityService.sellUnits(
        productId,
        parseInt(sellQuantity)
      );
      if (response.success) {
        showSuccess(`Successfully sold ${sellQuantity} units`);
        setSellQuantity("");
        loadData(); // Refresh data to show updated status
      } else {
        showError(response.message || "Failed to sell units");
      }
    } catch (error) {
      console.error("Error selling units:", error);
      showError(error.response?.data?.message || "Failed to sell units");
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (product) => {
    setEditingProduct(product._id);
    setEditForm({
      // totalUnits: product.availability.totalUnits || "",
      lowStockThreshold: product.availability.lowStockThreshold || "",
      trackingEnabled: product.availability.trackingEnabled ?? true,
    });
  };

  const getStatusColor = (status) => {
    if (!status) return "text-gray-600 bg-gray-100"; // Handle undefined status
    switch (status.toLowerCase()) {
      case "in-stock":
        return "text-green-600 bg-green-100";
      case "low-stock":
        return "text-yellow-600 bg-yellow-100";
      case "out-of-stock":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = "blue" }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className={`text-2xl font-bold text-${color}-600`}>
            {value?.toLocaleString() || "0"}
          </p>
        </div>
        <Icon className={`h-8 w-8 text-${color}-500`} />
      </div>
    </div>
  );

  const handleCreateProduct = async () => {
    try {
      setLoading(true);

      // Validate required fields
      const requiredFields = [
        {
          field: createForm.basicData.legalName,
          message: "Legal name is required",
        },
        { field: createForm.basicData.cin, message: "CIN is required" },
        {
          field: createForm.weekData52.buyPrice !== undefined,
          message: "Buy price is required",
        },
        {
          field: createForm.weekData52.sellPrice !== undefined,
          message: "Sell price is required",
        },
        {
          field: createForm.availability.availableUnits !== undefined,
          message: "Available units is required",
        },
      ];

      for (const { field, message } of requiredFields) {
        if (!field && field !== 0) {
          showError(message);
          return;
        }
      }

      // Generate uniqueTitle if not provided
      const productData = {
        ...createForm,
        basicData: {
          ...createForm.basicData,
          uniqueTitle:
            createForm.basicData.uniqueTitle ||
            createForm.basicData.legalName.toLowerCase().replace(/\s+/g, "-"),
        },
        weekData52: {
          ...createForm.weekData52,
          buyPrice: parseFloat(createForm.weekData52.buyPrice) || 0,
          sellPrice: parseFloat(createForm.weekData52.sellPrice) || 0,
        },
        availability: {
          ...createForm.availability,
          availableUnits: parseFloat(createForm.availability.availableUnits) || 0,
          lowStockThreshold: parseFloat(createForm.availability.lowStockThreshold) || 0,
          totalUnits: parseFloat(createForm.availability.availableUnits) || 0,
          trackingEnabled: createForm.availability.trackingEnabled,
        },
      };

      const response = await productAvailabilityService.createProduct(
        productData
      );

      if (response.success) {
        showSuccess("Product created successfully");

        // If a logo file was chosen, upload it now that we have a uniqueTitle
        const createdUniqueTitle =
          productData.basicData.uniqueTitle;
        if (pendingLogoFile && createdUniqueTitle) {
          const logoResult = await productAvailabilityService.uploadProductLogo(
            createdUniqueTitle,
            pendingLogoFile
          );
          if (!logoResult.success) {
            showError("Product created but logo upload failed: " + logoResult.message);
          }
          setPendingLogoFile(null);
        }

        setShowCreateModal(false);

        // Reset form
        setCreateForm({
          basicData: {
            legalName: "",
            secondaryName: "",
            isin: "",
            cin: "",
            industry: "",
            sector: "",
            logo: "",
            dateOfIncorporation: null,
            website: "",
          },
          weekData52: {
            buyPrice: "",
            sellPrice: "",
          },
          availability: {
            totalUnits: "",
            availableUnits: "",
            lowStockThreshold: "",
            trackingEnabled: true,
          },
        });

        // Refresh data
        loadData();
      } else {
        showError(response.message || "Failed to create product");
      }
    } catch (error) {
      console.error("Error in handleCreateProduct:", error);
      showError(error.message || "Failed to create product");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProduct = async (uniqueTitle) => {
    try {
      setLoading(true);

      // Ensure we're sending the complete availability object
      const availableUnitsInput = parseFloat(currentEditingProduct.availability.availableUnits);
      const productData = {
        ...currentEditingProduct,
        weekData52: {
          ...currentEditingProduct.weekData52,
          buyPrice: parseFloat(currentEditingProduct.weekData52.buyPrice) || 0,
          sellPrice: parseFloat(currentEditingProduct.weekData52.sellPrice) || 0,
        },
        availability: {
          ...currentEditingProduct.availability,
          availableUnits: !isNaN(availableUnitsInput)
            ? availableUnitsInput
            : currentEditingProduct.availability.totalUnits -
            (currentEditingProduct.availability.reservedUnits || 0) -
            (currentEditingProduct.availability.soldUnits || 0),
          lowStockThreshold: parseFloat(currentEditingProduct.availability.lowStockThreshold) || 0,
        },
      };

      const response = await productAvailabilityService.updateProduct(
        uniqueTitle,
        productData
      );

      if (response.success) {
        showSuccess("Product updated successfully");
        setShowEditModal(false);

        // Force reload by resetting the page and refreshing data
        setCurrentPage(1);
        loadData();
      } else {
        throw new Error(response.message || "Failed to update product");
      }
    } catch (error) {
      console.error("Update product error:", error);
      showError(error.message || "Failed to update product");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      const response = await productAvailabilityService.deleteProduct(
        productId
      );
      if (response.success) {
        showSuccess("Product deleted successfully");
        setProductToDelete(null);
        loadData();
      } else {
        showError(response.message || "Failed to delete product");
      }
    } catch (error) {
      console.error("Delete product error:", error);
      showError(error.message || error.response?.data?.message || "Failed to delete product");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Add custom CSS for toast animation */}
      <style jsx>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Product Availability Manager
          </h1>
          <p className="text-gray-600">
            Manage inventory, track stock levels, and monitor product
            availability
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-6">
          <nav className="flex space-x-8 border-b border-gray-200">
            <button
              onClick={() => setActiveTab("overview")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "overview"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
            >
              <BarChart3 className="inline-block w-4 h-4 mr-2" />
              Overview
            </button>
            <button
              onClick={() => setActiveTab("low-stock")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "low-stock"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
            >
              <AlertTriangle className="inline-block w-4 h-4 mr-2" />
              Low Stock Products
            </button>
            <button
              onClick={() => setActiveTab("all-products")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "all-products"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
            >
              <List className="inline-block w-4 h-4 mr-2" />
              All Products
            </button>
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  title="Total Products"
                  value={stats?.totalProducts}
                  icon={Package}
                  color="blue"
                />
                <StatCard
                  title="Available Units"
                  value={stats?.availableUnits}
                  icon={TrendingUp}
                  color="green"
                />
              </div>
            )}

            {/* Stock Status Summary */}
            {stats && (
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Stock Status Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {stats.inStockCount}
                    </div>
                    <div className="text-sm text-green-600">In Stock</div>
                  </div>
                  <div className="text-center p-4 bg-yellow-50 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600">
                      {stats.lowStockCount}
                    </div>
                    <div className="text-sm text-yellow-600">Low Stock</div>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">
                      {stats.outOfStockCount}
                    </div>
                    <div className="text-sm text-red-600">Out of Stock</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Low Stock Products Tab */}
        {activeTab === "low-stock" && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center space-x-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={loadData}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Refresh</span>
                </button>
              </div>
            </div>

            {/* Products List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Product
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Available
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Threshold
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {lowStockProducts.map((product) => (
                        <tr key={product._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-8 w-8 rounded-lg overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center text-[10px] mr-3 flex-shrink-0">
                                {product.basicData?.logo ? (
                                  <img
                                    src={product.basicData.logo}
                                    alt={product.basicData.legalName}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div className={product.basicData?.logo ? "hidden" : "w-full h-full flex items-center justify-center bg-blue-100 text-blue-600 font-bold"}>
                                  {product.basicData.legalName.charAt(0).toUpperCase()}
                                </div>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {product.basicData.legalName}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {product.basicData.uniqueTitle}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                                product.availability.status
                              )}`}
                            >
                              {product.availability.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {editingProduct === product._id ? (
                              <input
                                type="number"
                                value={editForm.totalUnits}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    totalUnits: e.target.value,
                                  })
                                }
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            ) : (
                              `${product.availability.availableUnits} `
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {editingProduct === product._id ? (
                              <input
                                type="number"
                                value={editForm.lowStockThreshold}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    lowStockThreshold: e.target.value,
                                  })
                                }
                                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            ) : (
                              product.availability.lowStockThreshold
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            {editingProduct === product._id ? (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() =>
                                    handleUpdateAvailability(product._id)
                                  }
                                  className="text-green-600 hover:text-green-900"
                                >
                                  <Save className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => setEditingProduct(null)}
                                  className="text-gray-600 hover:text-gray-900"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-4">
                                <button
                                  onClick={() => startEditing(product)}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  <Edit3 className="h-4 w-4" />
                                </button>

                                {/* Reserve Units */}
                                <div className="flex items-center space-x-1">
                                  <input
                                    type="number"
                                    placeholder="Reserve"
                                    value={reserveQuantity}
                                    onFocus={(e) => e.target.select()}
                                    onChange={(e) =>
                                      setReserveQuantity(e.target.value)
                                    }
                                    className="w-16 px-1 py-1 border border-gray-300 rounded text-xs"
                                  />
                                  <button
                                    onClick={() =>
                                      handleReserveUnits(product._id)
                                    }
                                    className="text-yellow-600 hover:text-yellow-900"
                                  >
                                    <Minus className="h-4 w-4" />
                                  </button>
                                </div>

                                {/* Sell Units */}
                                <div className="flex items-center space-x-1">
                                  <input
                                    type="number"
                                    placeholder="Sell"
                                    value={sellQuantity}
                                    onFocus={(e) => e.target.select()}
                                    onChange={(e) =>
                                      setSellQuantity(e.target.value)
                                    }
                                    className="w-16 px-1 py-1 border border-gray-300 rounded text-xs"
                                  />
                                  <button
                                    onClick={() => handleSellUnits(product._id)}
                                    className="text-purple-600 hover:text-purple-900"
                                  >
                                    <Plus className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() =>
                        setCurrentPage(Math.max(1, currentPage - 1))
                      }
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage(
                          Math.min(pagination.totalPages, currentPage + 1)
                        )
                      }
                      disabled={currentPage === pagination.totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{" "}
                        <span className="font-medium">
                          {(currentPage - 1) * pagination.itemsPerPage + 1}
                        </span>{" "}
                        to{" "}
                        <span className="font-medium">
                          {Math.min(
                            currentPage * pagination.itemsPerPage,
                            pagination.totalItems
                          )}
                        </span>{" "}
                        of{" "}
                        <span className="font-medium">
                          {pagination.totalItems}
                        </span>{" "}
                        results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        {Array.from(
                          { length: pagination.totalPages },
                          (_, i) => i + 1
                        ).map((page) => (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === currentPage
                              ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                              : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                              }`}
                          >
                            {page}
                          </button>
                        ))}
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* All Products Tab */}
        {activeTab === "all-products" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search all products..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Product
              </button>
            </div>

            {/* Products Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Product
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Buy/Sell Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sector/Industry
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Available
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {allProducts.map((product) => (
                        <tr key={product._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-8 w-8 rounded-lg overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center text-[10px] mr-3 flex-shrink-0">
                                {product.basicData?.logo ? (
                                  <img
                                    src={product.basicData.logo}
                                    alt={product.basicData.legalName}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div className={product.basicData?.logo ? "hidden" : "w-full h-full flex items-center justify-center bg-blue-100 text-blue-600 font-bold"}>
                                  {product.basicData?.legalName.charAt(0).toUpperCase()}
                                </div>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {product.basicData?.legalName}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {product.basicData?.uniqueTitle}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              Buy: ₹
                              {product.weekData52?.buyPrice?.toLocaleString() ||
                                "N/A"}
                            </div>
                            <div className="text-sm text-gray-500">
                              Sell: ₹
                              {product.weekData52?.sellPrice?.toLocaleString() ||
                                "N/A"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {product.basicData?.sector}
                            </div>
                            <div className="text-sm text-gray-500">
                              {product.basicData?.industry}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col items-start">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                                  product.availability?.status
                                )}`}
                              >
                                {product.availability?.status || "in-stock"}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {product.availability?.availableUnits || 0}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <button
                              onClick={() => {
                                const safeProductData =
                                  getSafeProductData(product);
                                setCurrentEditingProduct(safeProductData);
                                setShowEditModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setProductToDelete(product)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() =>
                        setCurrentPage(Math.max(1, currentPage - 1))
                      }
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage(
                          Math.min(pagination.totalPages, currentPage + 1)
                        )
                      }
                      disabled={currentPage === pagination.totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{" "}
                        <span className="font-medium">
                          {(currentPage - 1) * pagination.itemsPerPage + 1}
                        </span>{" "}
                        to{" "}
                        <span className="font-medium">
                          {Math.min(
                            currentPage * pagination.itemsPerPage,
                            pagination.totalItems
                          )}
                        </span>{" "}
                        of{" "}
                        <span className="font-medium">
                          {pagination.totalItems}
                        </span>{" "}
                        results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        {Array.from(
                          { length: pagination.totalPages },
                          (_, i) => i + 1
                        ).map((page) => (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === currentPage
                              ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                              : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                              }`}
                          >
                            {page}
                          </button>
                        ))}
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Create Product Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">Create New Product</h2>

              {/* Basic Data Section */}
              <div className="mb-6 border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Legal Name*
                    </label>
                    <input
                      type="text"
                      value={createForm.basicData.legalName}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          basicData: {
                            ...createForm.basicData,
                            legalName: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Secondary Name
                    </label>
                    <input
                      type="text"
                      value={createForm.basicData.secondaryName}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          basicData: {
                            ...createForm.basicData,
                            secondaryName: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      ISIN
                    </label>
                    <input
                      type="text"
                      value={createForm.basicData.isin}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          basicData: {
                            ...createForm.basicData,
                            isin: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      CIN*
                    </label>
                    <input
                      type="text"
                      value={createForm.basicData.cin}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          basicData: {
                            ...createForm.basicData,
                            cin: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Industry
                    </label>
                    <input
                      type="text"
                      value={createForm.basicData.industry}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          basicData: {
                            ...createForm.basicData,
                            industry: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Sector
                    </label>
                    <input
                      type="text"
                      value={createForm.basicData.sector}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          basicData: {
                            ...createForm.basicData,
                            sector: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Product Logo
                    </label>
                    <div className="mt-1 flex items-center space-x-4">
                      <label
                        htmlFor="create-logo-input"
                        className="cursor-pointer flex items-center space-x-2 px-4 py-2 border border-dashed border-gray-400 rounded-md hover:border-blue-500 hover:bg-blue-50 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-gray-600">
                          {createForm.basicData.logo ? "Change Logo" : "Choose Logo File"}
                        </span>
                        <input
                          id="create-logo-input"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleCreateLogoChange}
                        />
                      </label>
                      {createForm.basicData.logo && (
                        <div className="relative h-14 w-14">
                          <div className="h-14 w-14 rounded border border-gray-200 bg-gray-50 p-1">
                            <img
                              src={createForm.basicData.logo}
                              alt="Logo preview"
                              className="h-full w-full object-contain"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setCreateForm({
                                ...createForm,
                                basicData: { ...createForm.basicData, logo: "" },
                              })
                            }
                            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full h-4 w-4 flex items-center justify-center text-xs leading-none"
                            title="Remove logo"
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-400">PNG, JPG, SVG — max 2 MB</p>
                  </div>
                </div>
              </div>

              {/* Pricing Data Section */}
              <div className="mb-6 border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">
                  Pricing Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Buy Price*
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={createForm.weekData52.buyPrice}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          weekData52: {
                            ...createForm.weekData52,
                            buyPrice: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Sell Price*
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={createForm.weekData52.sellPrice}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          weekData52: {
                            ...createForm.weekData52,
                            sellPrice: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Availability Section */}
              <div className="mb-6 border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">
                  Inventory Availability
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Available Units*
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={createForm.availability.availableUnits}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          availability: {
                            ...createForm.availability,
                            availableUnits: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Low Stock Threshold
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={createForm.availability.lowStockThreshold}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          availability: {
                            ...createForm.availability,
                            lowStockThreshold: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={createForm.availability.trackingEnabled}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          availability: {
                            ...createForm.availability,
                            trackingEnabled: e.target.checked,
                          },
                        })
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-700">
                      Track Inventory
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateProduct}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center"
                  disabled={loading}
                >
                  {loading && (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Create Product
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Product Modal */}
        {showEditModal && currentEditingProduct && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">Edit Product</h2>

              {/* Basic Data Section */}
              <div className="mb-6 border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Legal Name*
                    </label>
                    <input
                      type="text"
                      value={currentEditingProduct.basicData.legalName}
                      onChange={(e) =>
                        setCurrentEditingProduct({
                          ...currentEditingProduct,
                          basicData: {
                            ...currentEditingProduct.basicData,
                            legalName: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>





                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Secondary Name
                    </label>
                    <input
                      type="text"
                      value={currentEditingProduct.basicData.secondaryName}
                      onChange={(e) =>
                        setCurrentEditingProduct({
                          ...currentEditingProduct,
                          basicData: {
                            ...currentEditingProduct.basicData,
                            secondaryName: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      ISIN (International Securities Identification Number)
                    </label>
                    <input
                      type="text"
                      value={currentEditingProduct.basicData.isin}
                      onChange={(e) =>
                        setCurrentEditingProduct({
                          ...currentEditingProduct,
                          basicData: {
                            ...currentEditingProduct.basicData,
                            isin: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      CIN (Corporate Identification Number)*
                    </label>
                    <input
                      type="text"
                      value={currentEditingProduct.basicData.cin}
                      onChange={(e) =>
                        setCurrentEditingProduct({
                          ...currentEditingProduct,
                          basicData: {
                            ...currentEditingProduct.basicData,
                            cin: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Industry
                    </label>
                    <input
                      type="text"
                      value={currentEditingProduct.basicData.industry}
                      onChange={(e) =>
                        setCurrentEditingProduct({
                          ...currentEditingProduct,
                          basicData: {
                            ...currentEditingProduct.basicData,
                            industry: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Sector
                    </label>
                    <input
                      type="text"
                      value={currentEditingProduct.basicData.sector}
                      onChange={(e) =>
                        setCurrentEditingProduct({
                          ...currentEditingProduct,
                          basicData: {
                            ...currentEditingProduct.basicData,
                            sector: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Product Logo
                    </label>
                    <div className="mt-1 flex items-center space-x-4">
                      <label
                        htmlFor="edit-logo-input"
                        className="cursor-pointer flex items-center space-x-2 px-4 py-2 border border-dashed border-gray-400 rounded-md hover:border-blue-500 hover:bg-blue-50 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-gray-600">
                          {currentEditingProduct.basicData.logo ? "Change Logo" : "Choose Logo File"}
                        </span>
                        <input
                          id="edit-logo-input"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) =>
                            handleEditLogoChange(
                              e,
                              currentEditingProduct.basicData.uniqueTitle
                            )
                          }
                        />
                      </label>
                      {currentEditingProduct.basicData.logo && (
                        <div className="relative h-14 w-14">
                          <div className="h-14 w-14 rounded border border-gray-200 bg-gray-50 p-1">
                            <img
                              src={currentEditingProduct.basicData.logo}
                              alt="Logo preview"
                              className="h-full w-full object-contain"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setCurrentEditingProduct({
                                ...currentEditingProduct,
                                basicData: { ...currentEditingProduct.basicData, logo: "" },
                              })
                            }
                            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full h-4 w-4 flex items-center justify-center text-xs leading-none"
                            title="Remove logo"
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-400">PNG, JPG, SVG — max 2 MB</p>
                  </div>
                </div>
              </div>

              {/* Pricing Data Section */}
              <div className="mb-6 border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">
                  Pricing Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Buy Price*
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={currentEditingProduct.weekData52.buyPrice}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) =>
                        setCurrentEditingProduct({
                          ...currentEditingProduct,
                          weekData52: {
                            ...currentEditingProduct.weekData52,
                            buyPrice: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Sell Price*
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={currentEditingProduct.weekData52.sellPrice}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) =>
                        setCurrentEditingProduct({
                          ...currentEditingProduct,
                          weekData52: {
                            ...currentEditingProduct.weekData52,
                            sellPrice: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Availability Section */}
              <div className="mb-6 border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">
                  Inventory Availability
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Available Units*
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={
                        currentEditingProduct?.availability?.availableUnits ?? ""
                      }
                      onFocus={(e) => e.target.select()}
                      onChange={(e) =>
                        setCurrentEditingProduct({
                          ...currentEditingProduct,
                          availability: {
                            ...currentEditingProduct.availability,
                            availableUnits: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Low Stock Threshold
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={
                        currentEditingProduct.availability.lowStockThreshold ?? ""
                      }
                      onFocus={(e) => e.target.select()}
                      onChange={(e) =>
                        setCurrentEditingProduct({
                          ...currentEditingProduct,
                          availability: {
                            ...currentEditingProduct.availability,
                            lowStockThreshold: e.target.value,
                          },
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={
                        currentEditingProduct.availability.trackingEnabled
                      }
                      onChange={(e) =>
                        setCurrentEditingProduct({
                          ...currentEditingProduct,
                          availability: {
                            ...currentEditingProduct.availability,
                            trackingEnabled: e.target.checked,
                          },
                        })
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-700">
                      Track Inventory
                    </label>
                  </div>

                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    handleUpdateProduct(
                      currentEditingProduct.basicData.uniqueTitle
                    )
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {productToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
              <p>
                Are you sure you want to delete{" "}
                {productToDelete.basicData.legalName}?
              </p>
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => setProductToDelete(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    handleDeleteProduct(productToDelete._id)
                  }
                  className="px-4 py-2 bg-red-600 text-white rounded-lg"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast Container */}
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </div >
  );
};

export default ProductAvailabilityManager;




